/**
 * Pure capability resolution engine.
 *
 * `resolveCapabilities(inputs)` is a deterministic, side-effect-free function of
 * (catalog + billing entitlements + edition grants + per-scope overrides). It
 * mirrors `buildCapabilityContext` in being pure and trivially unit-testable,
 * and it is the ONLY place feature availability is decided.
 *
 * Design invariants:
 *  - Specificity-ordered merge: a more specific scope wins outright over a
 *    broader one (a user override beats a workspace override). Within one scope
 *    layer, `disable` beats `enable`.
 *  - A GLOBAL `disable` is absolute: the kill switch and the go-live
 *    hold-back cannot be lifted by any narrower enable (workspace, user,
 *    programme). Founder decision 2026-10-05 ("global hold wins"); the server
 *    gates (`_shared/capabilityGate.ts`, `_shared/domain/vision/planGate.ts`
 *    for Vision, Verified and embed) apply the same rule, so the app never
 *    shows what the server refuses. Release = delete the global row.
 *  - Hard floors that no override can raise: internal-only outside internal
 *    contexts; a bound billing feature reported `false`; and non-`full` billing
 *    access state for write-tagged capabilities. Billing stays authoritative.
 *  - Dependency closure (Pass 2): a node is only truly enabled if ALL its
 *    `dependsOn` resolve enabled — matching `computeDisableImpact` in the admin
 *    preview.
 *
 * Always included: a node with `alwaysIncluded` ignores every grant and
 * override (it cannot be switched off), keeping only the hard floors.
 *
 * Defaults-on: with no grants and no overrides, every active non-beta node
 * resolves enabled, so the foundation ships with zero user-visible change.
 */
import type { WorkspaceEntitlementsSnapshot } from '@/lib/billing/types';
import { getAccessState, isBillingFeatureEnabled } from '@/lib/billing/entitlementResolver';
import type { CapabilityEffect, CapabilityScopeType } from '@/lib/capabilities/contracts';
import { CATALOG, CATALOG_VERSION, type CapabilityNodeKey } from '@/lib/capabilities/catalog';
import { topoOrder } from '@/lib/capabilities/dependencyGraph';

export interface OverrideRow {
  scopeType: CapabilityScopeType;
  scopeId: string;
  capabilityKey: CapabilityNodeKey;
  effect: CapabilityEffect;
}

export interface ResolveInputs {
  /** Billing snapshot = the edition/subscription scope input. Never duplicated by the CMP. */
  entitlements: WorkspaceEntitlementsSnapshot | null;
  /** Rows from `edition_capability_grants` for the active plan_key. */
  editionGrants: readonly OverrideRow[];
  /** All applicable `capability_overrides` rows (already scope-filtered by the RPC). */
  overrides: readonly OverrideRow[];
  /** Deployment environment, e.g. 'production'. */
  environment: string;
  /** True only for internal-dev builds / users. Gates `internalOnly` nodes. */
  isInternalContext: boolean;
  /** Beta programmes the current context is enrolled in. */
  betaProgrammes: readonly string[];
  /**
   * Capabilities no sold plan includes yet (on for Developer only, tracker 47).
   * One the workspace's plan switches off reads `unreleased`, not `edition`: it
   * is hidden like a held-back feature, never offered as an upgrade.
   */
  unreleasedCapabilities?: ReadonlySet<string>;
}

export type ResolvedSource =
  | 'catalog'
  | CapabilityScopeType
  | 'billing_floor'
  | 'internal_floor'
  | 'dependency'
  /** Switched off by the plan AND by every sold plan: still in testing on Developer (tracker 47), so hidden, never sold. */
  | 'unreleased';

export interface ResolvedCapability {
  key: CapabilityNodeKey;
  enabled: boolean;
  /** The winning scope/floor, for admin explainability. */
  source: ResolvedSource;
  reason?: string;
}

export interface ResolvedCapabilitySet {
  catalogVersion: string;
  byKey: Record<CapabilityNodeKey, ResolvedCapability>;
  has: (key: CapabilityNodeKey) => boolean;
}

/** Specificity rank: higher wins. `beta_programme` sits just above `edition`. */
const SCOPE_RANK: Record<CapabilityScopeType, number> = {
  global: 1,
  environment: 2,
  edition: 3,
  beta_programme: 3.5,
  industry: 4,
  journey_pack: 5,
  workspace: 6,
  role: 7,
  user: 8,
  internal_dev: 9,
};

export function resolveCapabilities(inputs: ResolveInputs): ResolvedCapabilitySet {
  const byKey = {} as Record<CapabilityNodeKey, ResolvedCapability>;
  const accessState = getAccessState(inputs.entitlements);

  // Group override rows by capability key once (O(overrides)).
  const overridesByKey = new Map<CapabilityNodeKey, OverrideRow[]>();
  const pushRow = (row: OverrideRow) => {
    const list = overridesByKey.get(row.capabilityKey);
    if (list) list.push(row);
    else overridesByKey.set(row.capabilityKey, [row]);
  };
  for (const row of inputs.editionGrants) pushRow({ ...row, scopeType: 'edition' });
  for (const row of inputs.overrides) pushRow(row);

  // ── Pass 1 — per-node base + scope merge ──────────────────────────────────
  for (const node of Object.values(CATALOG)) {
    const key = node.key as CapabilityNodeKey;
    // Base: active non-beta nodes default enabled. Beta nodes stay off until a
    // beta_programme scope enables them.
    let enabled = node.active && !node.betaFlag;
    let winningRank = 0;
    let source: ResolvedSource = 'catalog';

    const apply = (rank: number, effect: CapabilityEffect, src: ResolvedSource) => {
      // More specific wins; on a tie within a layer, disable wins.
      if (rank > winningRank || (rank === winningRank && effect === 'disable')) {
        enabled = effect === 'enable';
        winningRank = rank;
        source = src;
      }
    };

    // "Always included" rows (tracker 34.05) are part of every plan: no grant
    // or override switches them off, so nothing that depends on them is
    // switched off through them either. The server gates skip them the same way.
    const rows = node.alwaysIncluded ? [] : (overridesByKey.get(key) ?? []);
    for (const row of rows) {
      if (row.scopeType === 'environment' && row.scopeId !== inputs.environment) continue;
      if (row.scopeType === 'beta_programme' && !inputs.betaProgrammes.includes(row.scopeId)) continue;
      if (row.scopeType === 'internal_dev' && !inputs.isInternalContext) continue;
      apply(SCOPE_RANK[row.scopeType], row.effect, row.scopeType);
    }
    if (rows.some((row) => row.scopeType === 'global' && row.effect === 'disable')) {
      enabled = false;
      source = 'global';
    }

    // ── Hard floors (cannot be raised by any override) ──
    if (node.internalOnly && !inputs.isInternalContext) {
      enabled = false;
      source = 'internal_floor';
    }
    if (node.layer === 'capability' && node.billingFeatureKey) {
      if (!isBillingFeatureEnabled(inputs.entitlements, node.billingFeatureKey)) {
        enabled = false;
        source = 'billing_floor';
      }
    }
    if (accessState !== 'full' && node.tags.includes('write')) {
      enabled = false;
      source = 'billing_floor';
    }

    // Off through the plan, and no sold plan has it: still in testing, not for sale.
    // (`source` is set inside `apply`, so TypeScript's narrowing above does not see it.)
    if (!enabled && (source as ResolvedSource) === 'edition' && inputs.unreleasedCapabilities?.has(key)) {
      source = 'unreleased';
    }

    byKey[key] = { key, enabled, source };
  }

  // ── Pass 2 — dependency closure in topological order ──────────────────────
  for (const key of topoOrder()) {
    const node = CATALOG[key];
    if (!node) continue;
    const current = byKey[key];
    if (!current?.enabled) continue;
    for (const dep of node.dependsOn) {
      const depResolved = byKey[dep as CapabilityNodeKey];
      if (!depResolved?.enabled) {
        byKey[key] = {
          key,
          enabled: false,
          source: 'dependency',
          reason: `blocked by ${dep}`,
        };
        break;
      }
    }
  }

  return {
    catalogVersion: CATALOG_VERSION,
    byKey,
    has: (key) => byKey[key]?.enabled === true,
  };
}
